import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore, useFeature } from '@/app/stores';
import type { FeatureKey } from '@/types';
import { PageLoader } from '@/components/ui/States';
import { EmptyState } from '@/components/ui/States';
import { ButtonLink } from '@/components/ui/Button';
import { Container } from '@/components/ui/Layout';
import { PowerOff } from 'lucide-react';

/** Redirects anonymous visitors to /login and remembers where they were going. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { user, ready } = useAuthStore();
  const loc = useLocation();
  if (!ready) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children ?? <Outlet />}</>;
}

export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, ready } = useAuthStore();
  if (!ready) return <PageLoader />;
  return user ? <Navigate to="/account" replace /> : <>{children}</>;
}

/** Route-level module gate: shows a "module disabled" page when the tenant switched the feature off. */
export function FeatureGate({ feature, children }: { feature: FeatureKey; children?: ReactNode }) {
  const on = useFeature(feature);
  if (on) return <>{children ?? <Outlet />}</>;
  return (
    <Container>
      <EmptyState
        icon={PowerOff} title="This section isn't available"
        description="This store hasn't enabled this feature."
        action={<ButtonLink to="/" variant="outline">Back to home</ButtonLink>}
      />
    </Container>
  );
}
